// src/pages/PersonalizeDraft.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import ReactMarkdown from "react-markdown";

function PersonalizeDraft() {
  const navigate = useNavigate();
  // Retrieve userId from authData in localStorage
  const authData = localStorage.getItem("authData");
  const parsedAuthData = authData ? JSON.parse(authData) : null;
  const userId = parsedAuthData?.user?.sub;
  const [companyUrl, setCompanyUrl] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [stage, setStage] = useState("");
  const [purchaseHistory, setPurchaseHistory] = useState("");
  const [previousCommunication, setPreviousCommunication] = useState("");
  const [personalizedDraft, setPersonalizedDraft] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Copy State
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!userId) {
      setError("You are not logged in. Please log in.");
    }

    // Retrieve draftId from localStorage
    const storedDraftId = localStorage.getItem("draftId");

    if (!storedDraftId || !userId) {
      navigate("/", { replace: true });
    } else {
      setDraftId(storedDraftId);
    }
  }, [navigate, userId]);

  const handlePersonalize = async () => {
    if (!recipientName.trim()) {
      setError("Please provide the recipient's name.");
      return;
    }

    if (
      !title.trim() &&
      !companyUrl.trim() &&
      !linkedinUrl.trim() &&
      !city.trim() &&
      !purchaseHistory.trim() &&
      !previousCommunication.trim()
    ) {
      setError(
        "Please provide at least one additional detail to personalize the draft."
      );
      return;
    }

    if (!draftId || !userId) {
      setError("Draft ID or User ID is missing.");
      return;
    }

    setIsLoading(true);
    setError(null);

    const innerPayload = {
      draftId,
      userId,
      linkedinUrl,
      companyUrl,
      recipientName,
      title,
      city,
      stage,
      purchaseHistory,
      previousCommunication,
    };

    const fullPayload = {
      body: JSON.stringify(innerPayload),
    };

    try {
      const PERSONALIZE_API_URL =
        process.env.REACT_APP_PERSONALIZE_URL;

      const response = await fetch(PERSONALIZE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fullPayload),
      });

      if (!response.ok) {
        throw new Error("Failed to personalize draft.");
      }

      const data = await response.json();
      const parsedData =
        typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      // Capture the new draftId from the response
      const { draftId: newDraftId, personalizedDraft: newDraft } = parsedData;

      if (!newDraft) {
        throw new Error("API response is missing personalizedDraft.");
      }

      // Update the draftId with the new value from the API response
      setDraftId(newDraftId);
      // Optionally, update localStorage with the new draftId
      localStorage.setItem("draftId", newDraftId);

      // Update the personalized draft display (if needed)
      setPersonalizedDraft(newDraft);
    } catch (err) {
      setError("Failed to personalize draft. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  // Helper function to remove double asterisks
  const removeDoubleAsterisks = (text) => {
    return text.replace(/\*\*/g, "");
  };

  // Helper function to construct the full text with recipient info
  const constructFullText = () => {
    let header = `Recipient Name: ${recipientName}\n`;

    if (linkedinUrl.trim()) {
      header += `LinkedIn Profile: ${linkedinUrl}\n`;
    }

    header += `\n---\n\n`; // Separator for clarity

    // Combine header with the personalized draft
    return `${header}${personalizedDraft}`;
  };

  // Handle Copy
  const handleCopy = () => {
    // Construct the full text with recipient info
    const fullText = constructFullText();

    // Remove double asterisks or any other markdown syntax if necessary
    const plainText = removeDoubleAsterisks(fullText);

    navigator.clipboard
      .writeText(plainText)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
      })
      .catch((err) => {
        setError("Failed to copy draft. Please try again.");
      });
  };

  // Handle Download
  const handleDownload = () => {
    // Construct the full text with recipient info
    const fullText = constructFullText();

    // Remove double asterisks or any other markdown syntax if necessary
    const plainText = removeDoubleAsterisks(fullText);

    const blob = new Blob([plainText], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "personalized_draft.txt";
    link.click();
  };

  // Handle Next: Finalize Draft
  const handleNext = () => {
    if (!draftId) {
      setError("Draft ID is missing. Please personalize the draft before proceeding.");
      return;
    }
    // Pass only the updated draftId to the next screen
    navigate("/optimizeForSpam", { state: { draftId } });
  };

  // Optional: Real-time validation to enable/disable the "Apply Personalization" button
  const isFormValid =
    recipientName.trim() &&
    (title.trim() ||
      linkedinUrl.trim() ||
      companyUrl.trim() ||
      city.trim() ||
      purchaseHistory.trim() ||
      previousCommunication.trim());

  // Helper function to handle text area changes with character limit
  const handleTextAreaChange = (setter) => (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setter(value);
    }
  };

  return (
    <Layout>
      {/* 
        ===========================
        DASHBOARD STEPS SECTION
        ===========================
      */}
      <div className="py-2 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* On extra-small screens, stack steps vertically; on small screens and up, use a row */}
          <nav className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-x-4 text-xs py-1 px-3 bg-transparent">
            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-3 sm:w-4 h-3 sm:h-4 mr-1 text-[8px] sm:text-[10px] font-bold text-gray-700 bg-transparent border border-gray-700 rounded-full">
                1
              </span>
              Generic Draft
            </div>

            <span className="hidden sm:block w-4 h-[1px] bg-gray-300" />

            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-3 sm:w-4 h-3 sm:h-4 mr-1 text-[8px] sm:text-[10px] font-bold text-white bg-indigo-600 rounded-full">
                2
              </span>
              Personalized
            </div>

            <span className="hidden sm:block w-4 h-[1px] bg-gray-300" />

            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-3 sm:w-4 h-3 sm:h-4 mr-1 text-[8px] sm:text-[10px] font-bold text-gray-500 bg-transparent border border-gray-300 rounded-full">
                3
              </span>
              Optimized for Email
            </div>
          </nav>
        </div>
      </div>

      {/* 
        ===========================
        MAIN PAGE CONTENT
        ===========================
      */}
      <div className="container mx-auto mt-10 max-w-4xl bg-white p-6 shadow-md rounded-md">
        <h1 className="text-xl font-semibold mb-4">Personalize Your Draft</h1>
        <p className="text-gray-600 mb-6">
          Add some details to make your draft more personalized and engaging.
          At least two fields are required to enable Personalization.
        </p>

        {/* Recipient Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient or Entity Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 p-2 rounded-md"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="e.g., Alice or XYZ corp (required)"
            disabled={isLoading}
          />
        </div>

       
        {/* Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 p-2 rounded-md"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Senior Engineer (optional)"
            disabled={isLoading}
          />
        </div>

        {/* Company URL Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL (Company Website)
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 p-2 rounded-md"
            value={companyUrl}
            onChange={(e) => setCompanyUrl(e.target.value)}
            placeholder="e.g., https://www.example.com (optional)"
            disabled={isLoading}
          />
        </div>

        {/* Linkedin Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Linkedin Profile Url (LinkedIn Profile)
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 p-2 rounded-md"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            placeholder="e.g., https://www.linkedin.com/in/exampleprofile (optional)"
            disabled={isLoading}
          />
        </div>

        {/* City */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 p-2 rounded-md"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g., Wonderland (optional)"
            disabled={isLoading}
          />
        </div>

        {/* Purchase History */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purchase History
          </label>
          <textarea
            className="w-full border border-gray-300 p-2 rounded-md"
            rows="3"
            value={purchaseHistory}
            onChange={handleTextAreaChange(setPurchaseHistory)}
            placeholder="Purchased a premium subscription last month (optional)"
            disabled={isLoading}
            maxLength={1000}
          />
          <p className="text-sm text-gray-500 mt-1">
            {purchaseHistory.length}/1000 characters
          </p>
        </div>

        {/* Stage - sequence number */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stage (Sequence Number example stage1,1 etc)
          </label>
          <textarea
            className="w-full border border-gray-300 p-2 rounded-md"
            rows="3"
            value={stage}
            onChange={handleTextAreaChange(setStage)}
            placeholder="Purchased a premium subscription last month (optional)"
            disabled={isLoading}
            maxLength={1000}
          />
          
        </div>

        {/* Previous Communication */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reference to Previous Communication or LinkedIn Post
          </label>
          <textarea
            className="w-full border border-gray-300 p-2 rounded-md"
            rows="3"
            value={previousCommunication}
            onChange={handleTextAreaChange(setPreviousCommunication)}
            placeholder="Attended our last webinar and responded positively (optional)"
            disabled={isLoading}
            maxLength={1000}
          />
          <p className="text-sm text-gray-500 mt-1">
            {previousCommunication.length}/1000 characters
          </p>
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex items-center mb-4">
            <div className="spinner-border animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
            <span className="ml-2 text-blue-500">Personalizing...</span>
          </div>
        )}

        {/* Error Message */}
        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* Personalized Draft Display */}
        {personalizedDraft && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Personalized Draft</h3>
            <div className="relative border border-gray-300 p-4 rounded-md">
              <ReactMarkdown className="prose">
                {personalizedDraft}
              </ReactMarkdown>
              <button
                className="absolute top-2 right-2 bg-blue-500 text-white py-1 px-2 rounded-md text-sm hover:bg-blue-600"
                onClick={handleCopy}
                disabled={isLoading || !userId}
              >
                {isCopied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mb-4">
          <p className="text-sm text-gray-500 italic">
            Disclaimer: PreciseDraft utilizes artificial intelligence to assist
            in drafting documents. While we strive for high accuracy, the tool
            may produce errors. Please ensure you review all drafts thoroughly
            before finalizing.
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="flex flex-col sm:flex-row sm:space-x-2 w-full sm:w-auto mb-2 sm:mb-0">
            <button
              className={`bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 mr-2 w-full sm:w-auto ${
                !isFormValid || isLoading || !userId
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handlePersonalize}
              disabled={!isFormValid || isLoading || !userId}
            >
              Apply Personalization
            </button>

            <button
              className={`${
                !personalizedDraft
                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600 text-white"
              } py-2 px-4 rounded-md w-full sm:w-auto`}
              onClick={handleDownload}
              disabled={!personalizedDraft || isLoading || !userId}
            >
              Download Draft
            </button>
          </div>

          {/* Finalize Draft Button */}
          <button
            className={`${
              !personalizedDraft
                ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600 text-white"
            } py-2 px-4 rounded-md w-full sm:w-auto`}
            onClick={handleNext}
            disabled={!personalizedDraft || isLoading || !userId}
          >
            Next: Optimize for Email (Optional)
          </button>
        </div>
      </div>
    </Layout>
  );
}

export default PersonalizeDraft;
